import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { Image as ImageIcon, Upload, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface EventCoverMediaFieldsProps {
  thumbnailUrl?: string | null
  bannerUrl?: string | null
  thumbnailFile: File | null
  bannerFile: File | null
  onThumbnailFileChange: (file: File | null) => void
  onBannerFileChange: (file: File | null) => void
  onThumbnailRemove?: () => Promise<void> | void
  onBannerRemove?: () => Promise<void> | void
  removingThumbnail?: boolean
  removingBanner?: boolean
  disabled?: boolean
}

const imageSource = (value?: string | null) => {
  const resolved = String(value ?? '').trim()
  return resolved || null
}

export function EventCoverMediaFields({
  thumbnailUrl,
  bannerUrl,
  thumbnailFile,
  bannerFile,
  onThumbnailFileChange,
  onBannerFileChange,
  onThumbnailRemove,
  onBannerRemove,
  removingThumbnail = false,
  removingBanner = false,
  disabled = false,
}: EventCoverMediaFieldsProps) {
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null)
  const [bannerPreview, setBannerPreview] = useState<string | null>(null)

  useEffect(() => {
    if (!thumbnailFile) {
      setThumbnailPreview(null)
      return
    }

    const preview = URL.createObjectURL(thumbnailFile)
    setThumbnailPreview(preview)
    return () => URL.revokeObjectURL(preview)
  }, [thumbnailFile])

  useEffect(() => {
    if (!bannerFile) {
      setBannerPreview(null)
      return
    }

    const preview = URL.createObjectURL(bannerFile)
    setBannerPreview(preview)
    return () => URL.revokeObjectURL(preview)
  }, [bannerFile])

  const resolvedThumbnail = useMemo(() => thumbnailPreview || imageSource(thumbnailUrl), [thumbnailPreview, thumbnailUrl])
  const resolvedBanner = useMemo(() => bannerPreview || imageSource(bannerUrl), [bannerPreview, bannerUrl])

  const cropImage = async (file: File, targetAspectRatio: number): Promise<File> => {
    const imageUrl = URL.createObjectURL(file)
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const element = new Image()
        element.onload = () => resolve(element)
        element.onerror = () => reject(new Error('Failed to load image'))
        element.src = imageUrl
      })

      const sourceWidth = image.naturalWidth
      const sourceHeight = image.naturalHeight
      const sourceRatio = sourceWidth / sourceHeight

      let cropWidth = sourceWidth
      let cropHeight = sourceHeight
      if (sourceRatio > targetAspectRatio) {
        cropWidth = Math.round(sourceHeight * targetAspectRatio)
      } else {
        cropHeight = Math.round(sourceWidth / targetAspectRatio)
      }

      const offsetX = Math.max(0, Math.floor((sourceWidth - cropWidth) / 2))
      const offsetY = Math.max(0, Math.floor((sourceHeight - cropHeight) / 2))

      const canvas = document.createElement('canvas')
      canvas.width = cropWidth
      canvas.height = cropHeight
      const context = canvas.getContext('2d')
      if (!context) {
        return file
      }

      context.drawImage(
        image,
        offsetX,
        offsetY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight,
      )

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, file.type || 'image/jpeg', 0.95)
      })

      if (!blob) {
        return file
      }

      const extension = file.name.includes('.') ? file.name.split('.').pop() : 'jpg'
      const croppedName = `${file.name.replace(/\.[^.]+$/u, '')}-cropped.${extension}`
      return new File([blob], croppedName, { type: blob.type || file.type, lastModified: Date.now() })
    } finally {
      URL.revokeObjectURL(imageUrl)
    }
  }

  const handleImageSelect = async (
    event: ChangeEvent<HTMLInputElement>,
    onChange: (file: File | null) => void,
    targetAspectRatio: number,
  ) => {
    const file = event.target.files?.[0] ?? null
    event.target.value = ''
    if (!file) {
      onChange(null)
      return
    }
    const cropped = await cropImage(file, targetAspectRatio)
    onChange(cropped)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Event Display Images</CardTitle>
        <CardDescription>Set event thumbnail and banner directly on this event record.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-3 rounded-xl border border-border/70 p-4">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">Thumbnail</h3>
            <p className="mt-1 text-xs text-muted-foreground">Used on event list cards and compact displays.</p>
          </div>
          {resolvedThumbnail ? (
            <img src={resolvedThumbnail} alt="Event thumbnail" className="h-36 w-full rounded-lg object-cover" />
          ) : (
            <div className="flex h-36 items-center justify-center rounded-lg bg-muted/40 text-muted-foreground">
              <ImageIcon className="h-6 w-6" />
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={disabled || removingThumbnail}
                onChange={(event) => {
                  void handleImageSelect(event, onThumbnailFileChange, 1)
                }}
              />
              <span className="inline-flex h-9 cursor-pointer items-center rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground">
                <Upload className="mr-2 h-4 w-4" />
                {thumbnailFile ? 'Replace' : 'Upload'}
              </span>
            </label>
            {thumbnailFile ? (
              <Button type="button" variant="ghost" size="sm" disabled={disabled || removingThumbnail} onClick={() => onThumbnailFileChange(null)}>
                <X className="mr-2 h-4 w-4" />
                Clear
              </Button>
            ) : null}
            {!thumbnailFile && resolvedThumbnail && onThumbnailRemove ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled || removingThumbnail}
                onClick={() => {
                  void onThumbnailRemove()
                }}
              >
                <X className="mr-2 h-4 w-4" />
                {removingThumbnail ? 'Removing...' : 'Remove'}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="space-y-3 rounded-xl border border-border/70 p-4">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">Banner</h3>
            <p className="mt-1 text-xs text-muted-foreground">Used for event hero/banner areas and large displays.</p>
          </div>
          {resolvedBanner ? (
            <img src={resolvedBanner} alt="Event banner" className="h-36 w-full rounded-lg object-cover" />
          ) : (
            <div className="flex h-36 items-center justify-center rounded-lg bg-muted/40 text-muted-foreground">
              <ImageIcon className="h-6 w-6" />
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={disabled || removingBanner}
                onChange={(event) => {
                  void handleImageSelect(event, onBannerFileChange, 16 / 9)
                }}
              />
              <span className="inline-flex h-9 cursor-pointer items-center rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground">
                <Upload className="mr-2 h-4 w-4" />
                {bannerFile ? 'Replace' : 'Upload'}
              </span>
            </label>
            {bannerFile ? (
              <Button type="button" variant="ghost" size="sm" disabled={disabled || removingBanner} onClick={() => onBannerFileChange(null)}>
                <X className="mr-2 h-4 w-4" />
                Clear
              </Button>
            ) : null}
            {!bannerFile && resolvedBanner && onBannerRemove ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled || removingBanner}
                onClick={() => {
                  void onBannerRemove()
                }}
              >
                <X className="mr-2 h-4 w-4" />
                {removingBanner ? 'Removing...' : 'Remove'}
              </Button>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
